import store from "../../store/configureStore";
import { updateSmartLoader } from "../../actions/app/global";
import { setCreativeApiError } from "../../actions/app/creatives";
import { makeAuthHeaders } from "../oauth/auth";
import axios from "axios";
import _ from "lodash";
import { exportToExcel } from "../../utils/dataUtils";
import { fetchSizeService } from "./size";

const initialLoaderNew = {
  loading: true,
  title: "Creative submission in progress...",
  steps: [
    {
      title: "Uploading assets",
      inProgress: true,
      completed: false
    },
    {
      title: "Resolving conflicting creatives",
      inProgress: false,
      completed: false
    },
    {
      title: "Inserting creatives",
      inProgress: false,
      completed: false
    },
    {
      title: "Building creative assignments",
      inProgress: false,
      completed: false
    },
    {
      title: "Finalizing process",
      inProgress: false,
      completed: false
    }
  ]
};

const initialLoaderDefaultDimensions = {
  loading: true,
  title: "Default submission in progress...",
  steps: [
    {
      title: "Uploading asset",
      inProgress: true,
      completed: false
    },
    {
      title: "Inserting creative",
      inProgress: false,
      completed: false
    },
    {
      title: "Building default assignments",
      inProgress: false,
      completed: false
    }
  ]
};

const initialLoaderNoAssignment = {
  loading: true,
  title: "Creative submission in progress...",
  steps: [
    {
      title: "Uploading assets",
      inProgress: true,
      completed: false
    },
    {
      title: "Finalizing process",
      inProgress: false,
      completed: false
    }
  ]
};

const initialLoaderExisting = {
  loading: true,
  title: "Creative submission in progress...",
  steps: [
    {
      title: "Building creative assignments",
      inProgress: false,
      completed: false
    },
    {
      title: "Resolving conflicting creatives",
      inProgress: false,
      completed: false
    },
    {
      title: "Finalizing process",
      inProgress: false,
      completed: false
    }
  ]
};

const reorderCreatives = (apiCreatives, creativeOrder) => {
  /**
   * @param apiCreatives - array of creatives sent from API
   * @param creativeOrder - array of creative UI ID's that denote the order of creatives
   *
   * @returns an array of the newly ordered creatives adhearing to what was shown in the tool
   */

  let orderedCreatives = [];

  creativeOrder.map((id) => {
    let foundCreative = apiCreatives.find((c) => c.metadata.id === id);
    if (foundCreative) orderedCreatives.push(foundCreative);
  });

  return orderedCreatives;
};

const makeAdInfo = () => {
  let creativesState = store.getState().creatives;

  return {
    rotationType: creativesState.rotationType,
    weightDistribution: creativesState.weightDistribution,
    hardCutOff: creativesState.hardCutoff,
    startTime: creativesState.startDate.toJSON(),
    endTime: creativesState.endDate.toJSON(),
    dynamicTargetingKeys: creativesState.dynamicTargetingKeys.map(
      (key) => key.name
    )
  };
};

const postAssets = async (loader, creatives, advertiserId) => {
  let result = [];

  for (let i = 0; i < creatives.length; i++) {
    loader.steps[0].title = `Uploading asset ${i + 1}/${creatives.length}`;
    loader.steps[0].progress = 0;
    store.dispatch(updateSmartLoader({ ...loader }));

    let formData = new FormData();

    formData.set("advertiserId", advertiserId);

    formData.append(creatives[i].taxonomy.name, creatives[i].file);
    formData.append("names", creatives[i].taxonomy.name);

    let headers = await makeAuthHeaders();

    let response = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/dcm/creativeAsset`,
      formData,
      {
        headers,
        onUploadProgress: function (progressEvent) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );

          loader.steps[0].progress = percentCompleted;
          store.dispatch(updateSmartLoader({ ...loader }));
        }
      }
    );

    if (response.status !== 200) {
      if (response.status === 401) {
        throw new Error("UNAUTHORIZED");
      }

      throw new Error(response.data.message);
    }

    result = result.concat(response.data);
  }

  loader.steps[0].inProgress = false;
  loader.steps[0].completed = true;
  store.dispatch(updateSmartLoader({ ...loader }));

  return result;
};

const unassignPreviousAds = async (
  loader,
  loaderStep = 1,
  adInfo,
  placementIds
) => {
  loader.steps[loaderStep].inProgress = true;
  store.dispatch(updateSmartLoader({ ...loader }));
  let campaignStore = store.getState().dcmCampaigns;

  let headers = await makeAuthHeaders();

  headers["Content-Type"] = "application/json";

  let requestBody = {
    adInfo,
    placementIds
  };

  if (campaignStore.selectedId) {
    requestBody.campaignInfo = campaignStore.byId[campaignStore.selectedId];
    // let isoEndDate = new Date(requestBody.campaignInfo.endDate).toISOString();
    // requestBody.campaignInfo.endDate = isoEndDate;
  }

  let response = await fetch(
    `${process.env.REACT_APP_API_URL}/api/dcm/creative/unassign`,
    {
      method: "POST",
      headers,
      body: JSON.stringify(requestBody)
    }
  );

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error("UNAUTHORIZED");
    }

    let body = await response.json();
    throw new Error(body.message);
  }

  loader.steps[1].inProgress = false;
  loader.steps[1].completed = true;
  store.dispatch(updateSmartLoader({ ...loader }));
};

const postNewCreatives = async (
  loader,
  step,
  apiCreatives,
  placementProps,
  type
) => {
  loader.steps[step].inProgress = true;
  store.dispatch(updateSmartLoader({ ...loader }));

  let headers = await makeAuthHeaders();

  headers["Content-Type"] = "application/json";

  let response = await fetch(
    `${process.env.REACT_APP_API_URL}/api/dcm/creative/insertNew`,
    {
      method: "POST",
      headers,
      body: JSON.stringify({
        type,
        apiCreatives,
        placementProps
      })
    }
  );

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error("UNAUTHORIZED");
    }

    let body = await response.json();
    throw new Error(body.message);
  }

  loader.steps[step].inProgress = false;
  loader.steps[step].completed = true;
  store.dispatch(updateSmartLoader({ ...loader }));

  let body = await response.json();

  return body;
};

const postNewCreativesOneAtATime = async (
  loader,
  step,
  apiCreatives,
  placementProps,
  type
) => {
  loader.steps[step].inProgress = true;
  store.dispatch(updateSmartLoader({ ...loader }));

  let uploadedCreatives = [];
  let headers = await makeAuthHeaders();

  headers["Content-Type"] = "application/json";

  //initialize ignore list to prevent duplicate uploads of backup creatives
  let ignoreList = [];

  for (const creative of apiCreatives) {
    let backupCreative = null;
    let creativesToPush = [creative];

    if (ignoreList.includes(creative)) continue;

    // If the creative is a zip file, we need to upload the backup creative as well
    if (creative.originalFileName.includes(".zip")) {
      backupCreative = apiCreatives.find((c) => c.metadata.id === creative.metadata.backupId)
      ignoreList.push(backupCreative);
      creativesToPush.push(backupCreative);
    }

    let response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/dcm/creative/insertNew`,
      {
        method: "POST",
        headers,
        body: JSON.stringify({
          type,
          apiCreatives: creativesToPush,
          placementProps
        })
      }
    );

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error("UNAUTHORIZED");
      }

      let body = await response.json();
      throw new Error(body.message);
    }

    uploadedCreatives = uploadedCreatives.concat(await response.json());
  }

  loader.steps[step].inProgress = false;
  loader.steps[step].completed = true;
  store.dispatch(updateSmartLoader({ ...loader }));

  return uploadedCreatives;
};

const associateCreativesToCampaign = async (
  loader,
  loaderStep,
  apiCreatives,
  campaignId
) => {
  loader.steps[loaderStep].inProgress = true;
  store.dispatch(updateSmartLoader({ ...loader }));

  let headers = await makeAuthHeaders();

  headers["Content-Type"] = "application/json";

  let response = await fetch(
    `${process.env.REACT_APP_API_URL}/api/dcm/creative/associate`,
    {
      method: "POST",
      headers,
      body: JSON.stringify({
        apiCreatives,
        campaignId
      })
    }
  );

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error("UNAUTHORIZED");
    }

    let body = await response.json();
    throw new Error(body.message);
  }

  loader.steps[loaderStep].inProgress = false;
  loader.steps[loaderStep].completed = true;
  store.dispatch(updateSmartLoader({ ...loader }));
};

const insertAd = async (
  loader,
  loaderStep,
  type,
  adInfo,
  apiCreatives,
  placementIds,
  placementProps
) => {
  loader.steps[loaderStep].inProgress = true;
  store.dispatch(updateSmartLoader({ ...loader }));

  let headers = await makeAuthHeaders();

  headers["Content-Type"] = "application/json";

  let response = await fetch(
    `${process.env.REACT_APP_API_URL}/api/dcm/creative/ad`,
    {
      method: "POST",
      headers,
      body: JSON.stringify({
        type,
        placementProps,
        apiCreatives,
        placementIds,
        adInfo
      })
    }
  );

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error("UNAUTHORIZED");
    }

    let body = await response.json();
    throw new Error(body.message);
  }

  loader.steps[loaderStep].inProgress = false;
  loader.steps[loaderStep].completed = true;
  store.dispatch(updateSmartLoader({ ...loader }));
};

export const insertCreatives = async (creatives, assignedPlacements, type) => {
  let loader = _.defaultsDeep({}, initialLoaderNew);
  let uiCreativeOrder = creatives.map((c) => c.id);

  try {
    const creativesWithNewAssets = [];
    const creativesWithExistingAssets = [];

    for (let i = 0; i < creatives.length; i++) {
      if (creatives[i].file instanceof File) {
        creativesWithNewAssets.push(creatives[i]);
      } else {
        creativesWithExistingAssets.push(creatives[i]);
      }
    }

    const adInfo = makeAdInfo();
    const placementIds = assignedPlacements.map((placement) => placement.id);
    const placementProps = {
      advertiserId: assignedPlacements[0].advertiserId,
      campaignId: assignedPlacements[0].campaignId,
      compatibility: assignedPlacements[0].compatibility,
      size: assignedPlacements[0].size
    };

    const assets = await postAssets(
      loader,
      creativesWithNewAssets,
      placementProps.advertiserId
    );

    await unassignPreviousAds(loader, 1, adInfo, placementIds);

    let newApiCreatives = [];
    let existingApiCreatives = [];

    for (let i = 0; i < creativesWithNewAssets.length; i++) {
      let metadata = { ...creativesWithNewAssets[i] };

      let originalFileName = metadata.file.name;

      delete metadata.file;

      newApiCreatives.push({
        metadata,
        originalFileName,
        dcmAssetMetadata: assets[i]
      });
    }

    for (let i = 0; i < creativesWithExistingAssets.length; i++) {
      let metadata = { ...creativesWithExistingAssets[i] };

      let dcmCreative = metadata.dcmCreative;

      delete metadata.file;
      delete metadata.dcmId;
      delete metadata.dcmCreative;

      if (type === "TRACKING") {
        if (dcmCreative) {
          existingApiCreatives.push({
            metadata,
            dcmCreative,
            originalFileName: "N/A"
          });
        } else {
          newApiCreatives.push({
            metadata,
            originalFileName: "N/A",
            dcmAssetMetadata: assets[i]
          });
        }
      } else {
        existingApiCreatives.push({
          metadata,
          dcmCreative,
          originalFileName: "N/A"
        });
      }
    }

    let apiCreatives = await postNewCreatives(
      loader,
      2,
      newApiCreatives,
      placementProps,
      type
    );

    for (let i = 0; i < apiCreatives.length; i++) {
      delete apiCreatives[i].dcmAssetMetadata;
    }

    apiCreatives = reorderCreatives(
      apiCreatives.concat(existingApiCreatives),
      uiCreativeOrder
    );

    await associateCreativesToCampaign(
      loader,
      3,
      apiCreatives,
      placementProps.campaignId
    );
    await insertAd(
      loader,
      4,
      type,
      adInfo,
      apiCreatives,
      placementIds,
      placementProps
    );

    return apiCreatives;
  } catch (e) {
    console.log(e);
    store.dispatch(setCreativeApiError(e, true));
  } finally {
    setTimeout(() => {
      let newLoader = _.defaultsDeep({}, initialLoaderNew);
      newLoader.loading = false;
      newLoader.steps[0].inProgress = false;
      console.log("Reinitializing Loader");
      console.log(newLoader);
      store.dispatch(updateSmartLoader({ ...newLoader }));
    }, 1000);
  }
};

export const insertCreativesNoAssignments = async (
  creatives,
  creativeOptions,
  type
) => {
  let loader = _.defaultsDeep({}, initialLoaderNoAssignment);

  try {
    let sortedCreatives = {};

    // inserts all creative assets into every advertiser the user has access to
    // and sorts them based on advertiser
    for (let advertiserId of creativeOptions.advertiserIds) {
      loader = _.defaultsDeep({}, initialLoaderNoAssignment); // reset loader on each batch to prevent errors
      let assets = await postAssets(loader, creatives, advertiserId);

      let newApiCreatives = [];

      for (let i = 0; i < creatives.length; i++) {
        let metadata = { ...creatives[i] };

        let originalFileName = metadata.file.name;

        delete metadata.file;

        newApiCreatives.push({
          metadata,
          originalFileName,
          dcmAssetMetadata: assets[i],
          createdInPreloader: creatives[i].createdInPreloader
        });
      }

      let apiCreatives = await postNewCreativesOneAtATime(loader, 1, newApiCreatives, {
        advertiserId,
        compatibility: "DISPLAY"
      });

      for (let i = 0; i < apiCreatives.length; i++) {
        delete apiCreatives[i].dcmAssetMetadata;
      }

      sortedCreatives[advertiserId] = apiCreatives;
    }

    let exportCreatives = creatives
      .filter((c) => c.creativeRole === "PRIMARY")
      .map((c) => c.taxonomy.name);
    exportToExcel("Bart_Creatives_Export", exportCreatives);

    return sortedCreatives;
  } catch (e) {
    console.log(e);
    store.dispatch(setCreativeApiError(e, true));
  } finally {
    setTimeout(() => {
      let newLoader = _.defaultsDeep({}, initialLoaderNoAssignment);
      newLoader.loading = false;
      newLoader.steps[0].inProgress = false;
      console.log("Reinitializing Loader");
      console.log(newLoader);
      store.dispatch(updateSmartLoader({ ...newLoader }));
    }, 1000);
  }
};

export const assignCreatives = async (creatives, assignedPlacements) => {
  let loader = _.defaultsDeep({}, initialLoaderExisting);

  for (let i = 0; i < creatives.length; i++) {
    creatives[i].metadata = {
      taxonomy: creatives[i].taxonomy,
      weight: creatives[i].weight
    };

    delete creatives[i].taxonomy;
  }

  let adInfo = makeAdInfo();
  const placementIds = assignedPlacements.map((placement) => placement.id);
  const placementProps = {
    advertiserId: assignedPlacements[0].advertiserId,
    campaignId: assignedPlacements[0].campaignId,
    compatibility: assignedPlacements[0].compatibility,
    size: assignedPlacements[0].size
  };

  try {
    await associateCreativesToCampaign(
      loader,
      0,
      creatives,
      placementProps.campaignId
    );
    await unassignPreviousAds(loader, 1, adInfo, placementIds);

    await insertAd(
      loader,
      2,
      "DISPLAY",
      adInfo,
      creatives,
      placementIds,
      placementProps
    );
  } catch (e) {
    console.log(e);
    store.dispatch(setCreativeApiError(e, true));
  } finally {
    setTimeout(() => {
      loader = _.defaultsDeep({}, initialLoaderExisting);
      loader.loading = false;
      store.dispatch(updateSmartLoader({ ...loader }));
    }, 1000);
  }
};

export const fetchCreativesByIds = async (creativeIds) => {
  if (!creativeIds || creativeIds.length === 0) return [];

  try {
    let headers = await makeAuthHeaders();

    // Batch into chunks of 50 to avoid URL length limits and GCM API constraints
    const BATCH_SIZE = 50;
    const allResults = [];

    for (let i = 0; i < creativeIds.length; i += BATCH_SIZE) {
      const batch = creativeIds.slice(i, i + BATCH_SIZE);
      let response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/dcm/creatives?creativeIds=${batch.toString()}`,
        {
          method: "GET",
          headers
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error("UNAUTHORIZED");
        }

        let body = await response.json();
        throw new Error(body.message);
      }

      const data = await response.json();
      if (data && data.length > 0) allResults.push(...data);
    }

    return allResults;
  } catch (e) {
    console.log(e);
    throw e;
  }
};

export const fetchAssignedCreativesService = async (
  campaignId,
  creativeTypes = [],
  sizes = [],
  advertiserId = ""
) => {
  let queryParams = [];

  if (creativeTypes.length > 0) {
    queryParams.push(`creativeTypes=${creativeTypes.join(",")}`);
  }

  if (sizes.length > 0) {
    queryParams.push(`sizes=${sizes.join(",")}`);
  }

  if (advertiserId.length > 0) {
    queryParams.push(`advertiserId=${advertiserId}`);
  }

  let queryString = queryParams.join("&");

  if (queryString.length > 0) {
    queryString = `?${queryString}`;
  }

  try {
    let headers = await makeAuthHeaders();

    let response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/dcm/${campaignId}/creatives${queryString}`,
      {
        method: "GET",
        headers
      }
    );

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error("UNAUTHORIZED");
      }

      let body = await response.json();
      throw new Error(body.message);
    }

    return response.json();
  } catch (e) {
    console.log(e);
    throw e;
  }
};

export const insertRichMediaTemplates = async (advertisers, creatives) => {
  try {
    let headers = await makeAuthHeaders();

    headers["Content-Type"] = "application/json";

    let requestBody = {};
    let dcmSizes = {};

    for (let advertiser of advertisers) {
      for (let creative of creatives) {
        if (!dcmSizes[creative.taxonomy.dimensions]) {
          let parsedSizeArray = creative.taxonomy.dimensions.split("x");
          let parsedSize = {
            width: parsedSizeArray[0].trim(),
            height: parsedSizeArray[1].trim()
          };

          let dcmSize = await fetch(
            `${process.env.REACT_APP_API_URL}/api/dcm/size?width=${parsedSize.width}&height=${parsedSize.height}`,
            {
              method: "GET",
              headers
            }
          );

          dcmSizes[creative.taxonomy.dimensions] = await dcmSize.json();
        }

        requestBody = {
          creative: { ...creative, metadata: { taxonomy: creative.taxonomy } },
          metadata: { advertiser, size: dcmSizes[creative.taxonomy.dimensions] }
        };

        console.log(JSON.stringify(requestBody));

        let dcmCreative = await fetch(
          `${process.env.REACT_APP_API_URL}/api/dcm/creative/insert`,
          { method: "POST", headers, body: JSON.stringify(requestBody) }
        );

        console.log(await dcmCreative.json());
      }
    }
  } catch (e) {
    console.log(e);
    throw e;
  }
};

export const insertDefaultCreative = async (
  creatives,
  campaign,
  dimensions,
  type
) => {
  let loader = _.defaultsDeep({}, initialLoaderDefaultDimensions);
  let uiCreativeOrder = creatives.map((c) => c.id);

  try {
    const creativesWithNewAssets = [];
    const creativesWithExistingAssets = [];

    for (let i = 0; i < creatives.length; i++) {
      if (creatives[i].file instanceof File) {
        creativesWithNewAssets.push(creatives[i]);
      } else {
        creativesWithExistingAssets.push(creatives[i]);
      }
    }

    dimensions = dimensions.split("x"); //width is [0] height is [1]

    let dcmSize = await fetchSizeService(dimensions[0], dimensions[1]);
    let computedCompatibility = "DISPLAY";

    if ((dimensions[0] === "0") & (dimensions[1] === "0"))
      computedCompatibility = "IN_STREAM_VIDEO";
    // const adInfo = makeAdInfo();
    // const placementIds = assignedPlacements.map((placement) => placement.id);
    const placementProps = {
      advertiserId: campaign.advertiserId,
      campaignId: campaign.id,
      compatibility: computedCompatibility, // need to change to allow video as well
      size: dcmSize
    };

    const assets = await postAssets(
      loader,
      creativesWithNewAssets,
      placementProps.advertiserId
    );

    let newApiCreatives = [];
    let existingApiCreatives = [];

    for (let i = 0; i < creativesWithNewAssets.length; i++) {
      let metadata = { ...creativesWithNewAssets[i] };

      let originalFileName = metadata.file.name;

      delete metadata.file;

      newApiCreatives.push({
        metadata,
        originalFileName,
        dcmAssetMetadata: assets[i]
      });
    }

    for (let i = 0; i < creativesWithExistingAssets.length; i++) {
      let metadata = { ...creativesWithExistingAssets[i] };

      let dcmCreative = metadata.dcmCreative;

      delete metadata.file;
      delete metadata.dcmId;
      delete metadata.dcmCreative;

      if (type === "TRACKING") {
        if (dcmCreative) {
          existingApiCreatives.push({
            metadata,
            dcmCreative,
            originalFileName: "N/A"
          });
        } else {
          newApiCreatives.push({
            metadata,
            originalFileName: "N/A",
            dcmAssetMetadata: assets[i]
          });
        }
      } else {
        existingApiCreatives.push({
          metadata,
          dcmCreative,
          originalFileName: "N/A"
        });
      }
    }

    let apiCreatives = await postNewCreatives(
      loader,
      1,
      newApiCreatives,
      placementProps,
      type
    );

    for (let i = 0; i < apiCreatives.length; i++) {
      delete apiCreatives[i].dcmAssetMetadata;
    }

    apiCreatives = reorderCreatives(
      apiCreatives.concat(existingApiCreatives),
      uiCreativeOrder
    );

    await associateCreativesToCampaign(
      loader,
      2,
      apiCreatives,
      placementProps.campaignId
    );
    // await insertAd(
    //   loader,
    //   4,
    //   type,
    //   adInfo,
    //   apiCreatives
    //   // placementIds,
    //   // placementProps
    // );

    return apiCreatives;
  } catch (e) {
    console.log(e);
    store.dispatch(setCreativeApiError(e, true));
  } finally {
    setTimeout(() => {
      let newLoader = _.defaultsDeep({}, initialLoaderDefaultDimensions);
      newLoader.loading = false;
      newLoader.steps[0].inProgress = false;
      console.log("Reinitializing Loader");
      console.log(newLoader);
      store.dispatch(updateSmartLoader({ ...newLoader }));
    }, 1000);
  }
};